import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { HotelReservation, HotelPricing, TripData } from "./useTrips";
import type { Json } from "@/integrations/supabase/types";

// ─── Booking.com enrichment result ─────────────────────────────────────────

interface HotelEnrichmentResult {
  retail_price: number;
  currency: string;
  rating?: number;
  rating_label?: string;
  review_count?: number;
  stars?: number;
  hotel_image?: string;
  check_in_time?: string;
  check_out_time?: string;
  booking_url?: string;
  address?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Check if a hotel needs enrichment.
 */
export function needsHotelEnrichment(hotel: HotelReservation, pricings?: HotelPricing[]): boolean {
  // Check if we need price enrichment
  const needsPrice = !pricings?.some(p =>
    p.hotel_name === hotel.hotel_name && p.check_in_date === hotel.check_in_date
  );

  // Check if we need other enrichment fields
  const needsDetails = !hotel.enriched && (
    hotel.rating === undefined ||
    !hotel.hotel_image
  );

  return needsPrice || needsDetails;
}

/**
 * Apply enrichment data to a hotel reservation.
 */
function applyEnrichment(hotel: HotelReservation, enrichment: HotelEnrichmentResult): HotelReservation {
  return {
    ...hotel,
    rating: enrichment.rating ?? hotel.rating,
    rating_label: enrichment.rating_label || hotel.rating_label,
    review_count: enrichment.review_count ?? hotel.review_count,
    stars: enrichment.stars ?? hotel.stars,
    hotel_image: enrichment.hotel_image || hotel.hotel_image,
    check_in_time: enrichment.check_in_time || hotel.check_in_time,
    check_out_time: enrichment.check_out_time || hotel.check_out_time,
    booking_url: enrichment.booking_url || hotel.booking_url,
    // Only use API address as fallback if no address from email parsing
    address: hotel.address || enrichment.address || hotel.address,
    enriched: true,
    // Set retail price on the item's pricing field
    pricing: {
      ...(hotel.pricing || {}),
      retail_price: enrichment.retail_price,
      retail_currency: enrichment.currency,
      retail_fetched_at: new Date().toISOString(),
    },
  };
}

/**
 * Upsert a HotelPricing entry into the pricings array.
 */
function upsertHotelPricing(
  pricings: HotelPricing[],
  hotel: HotelReservation,
  enrichment: HotelEnrichmentResult,
): HotelPricing[] {
  // Remove existing entry for this hotel + check-in date
  const updated = pricings.filter(p =>
    !(p.hotel_name === hotel.hotel_name && p.check_in_date === hotel.check_in_date)
  );

  updated.push({
    retail_price: enrichment.retail_price,
    currency: enrichment.currency,
    hotel_name: hotel.hotel_name,
    check_in_date: hotel.check_in_date,
    confirmation_number: hotel.confirmation_number,
    fetched_at: new Date().toISOString(),
  });

  return updated;
}

// ─── API fetcher ────────────────────────────────────────────────────────────

/**
 * Fetch Booking.com price enrichment for a hotel.
 */
async function fetchHotelEnrichment(hotel: HotelReservation): Promise<HotelEnrichmentResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('enrich-hotel-price', {
      body: {
        hotel: {
          hotel_name: hotel.hotel_name,
          city: hotel.city,
          check_in_date: hotel.check_in_date,
          check_out_date: hotel.check_out_date,
          room_type: hotel.room_type,
        },
      },
    });

    if (error) {
      console.error('Error calling enrich-hotel-price:', error);
      return null;
    }

    return data?.enrichment as HotelEnrichmentResult | null;
  } catch (err) {
    console.error('Exception calling enrich-hotel-price:', err);
    return null;
  }
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Manual enrichment for a single hotel.
 */
export function useEnrichHotel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      hotelIndex,
      hotel,
    }: {
      tripId: string;
      hotelIndex: number;
      hotel: HotelReservation;
    }) => {
      const enrichment = await fetchHotelEnrichment(hotel);

      if (!enrichment) {
        return { enriched: false, message: 'No enrichment data found' };
      }

      // Fetch trip and update
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();

      if (fetchError) throw fetchError;

      const tripData = (trip.trip_data as TripData) || { flights: [], hotels: [], hotel_pricings: [], cars: [], activities: [], cruises: [] };
      const hotels = [...(tripData.hotels || [])];
      let pricings = [...(tripData.hotel_pricings || [])];

      // Apply enrichment to hotel
      hotels[hotelIndex] = applyEnrichment(hotels[hotelIndex], enrichment);

      // Add/update pricing
      pricings = upsertHotelPricing(pricings, hotel, enrichment);

      const updatedTripData = { ...tripData, hotels, hotel_pricings: pricings };

      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId);

      if (updateError) throw updateError;

      return { enriched: true, enrichment };
    },
    onSuccess: (result, variables) => {
      if (result.enriched) {
        queryClient.invalidateQueries({ queryKey: ["trip", variables.tripId] });
        queryClient.invalidateQueries({ queryKey: ["trips"] });
        toast.success("Hotel enriched with price and rating data");
      } else {
        toast.info(result.message || "No additional hotel data available");
      }
    },
    onError: (error) => {
      console.error("Hotel enrichment error:", error);
      toast.error("Failed to enrich hotel data");
    },
  });
}

/**
 * Batch enrichment for all hotels in a trip.
 */
export function useEnrichAllHotels() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      hotels,
    }: {
      tripId: string;
      hotels: HotelReservation[];
    }) => {
      // Run enrichment for each hotel in parallel
      const promises = hotels.map(async (hotel, index) => {
        const enrichment = await fetchHotelEnrichment(hotel);
        return { index, hotel, enrichment };
      });

      const results = await Promise.allSettled(promises);

      // Fetch current trip data
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();

      if (fetchError) throw fetchError;

      const tripData = (trip.trip_data as TripData) || { flights: [], hotels: [], hotel_pricings: [], cars: [], activities: [], cruises: [] };
      const updatedHotels = [...(tripData.hotels || [])];
      let pricings = [...(tripData.hotel_pricings || [])];

      let enrichedCount = 0;

      for (const result of results) {
        if (result.status === 'fulfilled' && result.value.enrichment) {
          const { index, hotel, enrichment } = result.value;
          updatedHotels[index] = applyEnrichment(updatedHotels[index], enrichment);
          pricings = upsertHotelPricing(pricings, hotel, enrichment);
          enrichedCount++;
        }
      }

      if (enrichedCount === 0) {
        return { enrichedCount: 0 };
      }

      const updatedTripData = { ...tripData, hotels: updatedHotels, hotel_pricings: pricings };

      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId);

      if (updateError) throw updateError;

      return { enrichedCount };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["trip", variables.tripId] });
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      if (result.enrichedCount > 0) {
        toast.success(`Enriched ${result.enrichedCount} hotel(s) with additional data`);
      } else {
        toast.info("No additional hotel data available from API");
      }
    },
    onError: (error) => {
      console.error("Hotel enrichment error:", error);
      toast.error("Failed to enrich hotel data");
    },
  });
}

/**
 * Auto-enrich a newly added hotel if it needs enrichment.
 * Silent on failure.
 */
export function useAutoEnrichHotel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      hotelIndex,
      hotel,
    }: {
      tripId: string;
      hotelIndex: number;
      hotel: HotelReservation;
    }) => {
      const enrichment = await fetchHotelEnrichment(hotel);

      if (!enrichment) {
        return { enriched: false, skipped: false };
      }

      // Fetch and update trip data
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();

      if (fetchError) throw fetchError;

      const tripData = (trip.trip_data as TripData) || { flights: [], hotels: [], hotel_pricings: [], cars: [], activities: [], cruises: [] };
      const hotels = [...(tripData.hotels || [])];
      let pricings = [...(tripData.hotel_pricings || [])];

      if (hotelIndex >= hotels.length) {
        return { enriched: false, skipped: true };
      }

      hotels[hotelIndex] = applyEnrichment(hotels[hotelIndex], enrichment);
      pricings = upsertHotelPricing(pricings, hotel, enrichment);

      const updatedTripData = { ...tripData, hotels, hotel_pricings: pricings };

      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId);

      if (updateError) throw updateError;

      return { enriched: true, enrichment };
    },
    onSuccess: (result, variables) => {
      if (result.enriched) {
        queryClient.invalidateQueries({ queryKey: ["trip", variables.tripId] });
        queryClient.invalidateQueries({ queryKey: ["trips"] });
        toast.success("Hotel data enriched automatically");
      }
    },
    onError: (error) => {
      // Silent failure for auto-enrich
      console.error("Auto-enrich hotel error:", error);
    },
  });
}
